import { isEmpty } from 'lodash-es';
import {
  forwardRef,
  type PropsWithChildren,
  type ReactElement,
  useMemo,
} from 'react';

import { HistogramChartLegend } from './components/legend/HistogramChartLegend.js';
import {
  DEFAULT_CHART_HEIGHT,
  DEFAULT_LOADING_STATE,
} from './constants/defaults.js';
import { HistogramChartInternal } from './HistogramChartInternal.js';
import { useHistogramChartConfig } from './hooks/useHistogramChartConfig.js';
import { HistogramConfigProvider } from './providers/HistogramConfigProvider.js';
import { HistogramDataProvider } from './providers/HistogramDataProvider.js';
import { HistogramFilteredDataProvider } from './providers/HistogramFilteredDataProvider.js';
import { HistogramStateSyncProvider } from './providers/HistogramStateSyncProvider.js';
import { StoreProvider } from './providers/StoreProvider.js';
import type { HistogramChartProps } from './types/histogram-props.js';
import type { HistogramChartRef } from './types/histogram-ref.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { ChartLayout } from '../core/components/chart-layout/ChartLayout.js';
import { useAutoLegendRefresh } from '../core/components/chart-layout/hooks/useAutoLegendRefresh.js';
import { getChartStatesTemplates } from '../core/components/chart-states/utils/get-chart-states-template.js';
import { OverlayProvider } from '../core/components/overlay-tooltip/contexts/OverlayContext.js';
import { TooltipProvider } from '../core/components/overlay-tooltip/contexts/TooltipContext.js';
import { ChartSeriesActionContext } from '../core/components/series-action/ChartSeriesActionContext.js';
import { AriaLabelingContext } from '../core/context/AriaLabelingContext.js';
import { coerceSizeValue } from '../core/utils/coerceSizeValue.js';

/**
 * Component name used for data-dt-component in behavioural tracking
 */
const COMPONENT_NAME_BEHAVIOURAL_TRACKING = 'HistogramChart';

/**
 * @public
 */
export const HistogramChartRoot = forwardRef<
  HistogramChartRef,
  PropsWithChildren<HistogramChartProps>
>((props, forwardedRef) => {
  const {
    children,
    data,
    height,
    width,
    colorPalette,
    truncationMode,
    loading = DEFAULT_LOADING_STATE,
    seriesActions,
    initialZoom,
    currentZoom,
    onZoomChange,
    infiniteZoom,
    className,
    style,
    valueRepresentation,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-details': ariaDetails,
  } = props;
  const [behaviorTrackingProps] = useBehavioralTrackingProps(props);
  const chartHeight = coerceSizeValue(height ?? DEFAULT_CHART_HEIGHT);
  const chartConfig = useHistogramChartConfig(children, {
    colorPalette,
    truncationMode,
    valueRepresentation,
    initialZoom,
    currentZoom,
    onZoomChange,
    infiniteZoom,
  });
  const { emptyState, errorState } = getChartStatesTemplates(props.children);

  const {
    position: legendPosition,
    onRatioChange,
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    ratio,
    initialRatio,
    hidden: isLegendHidden,
  } = chartConfig.legend;

  const legendRatio = initialRatio ?? ratio;

  const chartLayoutRef = useAutoLegendRefresh(data);
  const ariaLabelingValue = useMemo(
    () => ({
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      'aria-describedby': ariaDescribedBy,
      'aria-details': ariaDetails,
    }),
    [ariaLabel, ariaLabelledBy, ariaDescribedBy, ariaDetails],
  );

  return (
    <AriaLabelingContext.Provider value={ariaLabelingValue}>
      <HistogramConfigProvider config={chartConfig}>
        <HistogramDataProvider data={data}>
          <ChartSeriesActionContext.Provider
            value={{
              // We need a cast here as the provider expects unknown and the
              // react context will not be able to carry the generic anyways.
              chartSeriesActions: seriesActions as (
                series: unknown,
              ) => ReactElement<PropsWithChildren>,
            }}
          >
            <StoreProvider>
              <HistogramFilteredDataProvider>
                <HistogramStateSyncProvider
                  currentDomain={chartConfig.currentZoom as [number, number]}
                  initialDomain={chartConfig.initialZoom as [number, number]}
                  infiniteZoom={chartConfig.infiniteZoom}
                >
                  <OverlayProvider>
                    <TooltipProvider>
                      <ChartLayout
                        ref={chartLayoutRef}
                        chartHeight={chartHeight}
                        chartWidth={width}
                        isEmpty={isEmpty(data)}
                        emptyState={emptyState}
                        errorState={errorState}
                        showLoader={loading}
                        className={className}
                        style={style}
                        {...behaviorTrackingProps}
                        data-dtrum-mask={dataDtrumMask}
                        data-dtrum-allow={dataDtrumAllow}
                        data-dt-component={COMPONENT_NAME_BEHAVIOURAL_TRACKING}
                      >
                        <ChartLayout.Graph>
                          <HistogramChartInternal
                            ref={forwardedRef}
                            config={chartConfig}
                          />
                        </ChartLayout.Graph>
                        {!isLegendHidden && (
                          <ChartLayout.Legend
                            position={legendPosition}
                            initialRatio={legendRatio}
                            onResize={onRatioChange}
                          >
                            <HistogramChartLegend />
                          </ChartLayout.Legend>
                        )}
                      </ChartLayout>
                    </TooltipProvider>
                  </OverlayProvider>
                </HistogramStateSyncProvider>
              </HistogramFilteredDataProvider>
            </StoreProvider>
          </ChartSeriesActionContext.Provider>
        </HistogramDataProvider>
      </HistogramConfigProvider>
    </AriaLabelingContext.Provider>
  );
});

(
  HistogramChartRoot as typeof HistogramChartRoot & { displayName: string }
).displayName = 'HistogramChartRoot';
